import React, { useState, useEffect } from "react";
import { Facets, BasicFacetValue, BasicFacet, AttributeFacet } from "../types";
import { parseQueryParams } from "../utils/queryParams";
import { BasicFacetComponent } from "./BasicFacetComponent";
import { AttributeFacetComponent } from "./AttributeFacetComponent";
import { sanitizeAttributeIndexValue } from "../utils/stringUtils";
import { fetchFacetsData } from "../api/catalogApi";
import { eventBus, EVENTS } from "../utils/eventBus";
import { ShopsFacetCard } from "./ShopsFacetCard";

interface FacetCardNewProps {
  groupId: string;
  showMobileTrigger?: boolean;
}

export const FacetCardNew: React.FC<FacetCardNewProps> = ({
  groupId,
  showMobileTrigger = true,
}) => {
  const [facets, setFacets] = useState<Facets | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [expandedFacets, setExpandedFacets] = useState<Record<string, boolean>>(
    {}
  );
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, Set<string>>
  >({});

  // Load facets data
  const loadFacets = async (params?: any) => {
    try {
      // setLoading(true);
      const queryParams = params || parseQueryParams();
      const queryString = new URLSearchParams();

      // Add cp parameters
      if (queryParams.cp && queryParams.cp.length > 0) {
        queryParams.cp.forEach((value: string, index: number) => {
          queryString.append(`cp[${index}]`, value);
        });
      }

      // Add other parameters
      Object.entries(queryParams).forEach(([key, value]) => {
        if (key !== "cp") {
          if (Array.isArray(value)) {
            value.forEach((v) => queryString.append(key, v));
          } else {
            queryString.append(key, value as string);
          }
        }
      });

      const data = await fetchFacetsData(groupId, queryString.toString());
      setFacets(data);
    } catch (error) {
      console.error("Error loading facets:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load facets on mount
  useEffect(() => {
    loadFacets();
  }, [groupId]);

  // Listen for filter changes from URL updates
  useEffect(() => {
    const handleFiltersChanged = (params: any) => {
      const newFilters: Record<string, Set<string>> = {};

      params.cp.forEach((filter: string) => {
        const [facet, value] = filter.split(":");
        if (facet && value) {
          if (!newFilters[facet]) {
            newFilters[facet] = new Set<string>();
          }
          newFilters[facet].add(value);
        }
      });

      setSelectedFilters(newFilters);
      loadFacets(params);
    };

    eventBus.on(EVENTS.FILTERS_CHANGED, handleFiltersChanged);

    return () => {
      eventBus.off(EVENTS.FILTERS_CHANGED, handleFiltersChanged);
    };
  }, []);

  // Initialize selected filters from URL on component mount
  useEffect(() => {
    const params = parseQueryParams();
    const initialFilters: Record<string, Set<string>> = {};

    // Process cp parameters to extract facet and value
    params.cp.forEach((filter) => {
      const [facet, value] = filter.split(":");
      if (facet && value) {
        if (!initialFilters[facet]) {
          initialFilters[facet] = new Set<string>();
        }
        initialFilters[facet].add(value);
        if (facet === "attributes" || facet === "attributes_extra") {
          const parts = value.split("|");
          const attribute_name_key = parts[0] + "_" + parts[1];
          expandedFacets[attribute_name_key] = true;
        } else {
          expandedFacets[facet] = true;
        }
      }
    });

    setSelectedFilters(initialFilters);
  }, []);

  // Toggle facet expansion
  const toggleFacet = (facet: string) => {
    setExpandedFacets({
      ...expandedFacets,
      [facet]: !expandedFacets[facet],
    });
  };

  // Remove quotes from filter value
  const sanitizeFilterValue = (value: string): string => {
    if (typeof value !== "string") return String(value);
    if (value.startsWith('"') && value.endsWith('"')) {
      return value.substr(1, value.length - 1);
    }
    return value;
  };

  // Handle filter selection
  const handleFilterChange = (
    facetKey: string,
    value: BasicFacetValue,
    checked: boolean,
    facetType: string | null = null,
    isExtra = false
  ) => {
    let sanitizedValue = "";
    if (facetType?.toLocaleLowerCase() === "attribute") {
      sanitizedValue = sanitizeAttributeIndexValue(value.indexValue || "");
    } else {
      sanitizedValue = value.filter;
    }

    // Determine the actual facet key for attributes
    let actualFacetKey = facetKey;
    if (facetType === "Attribute") {
      actualFacetKey = isExtra ? "attributes_extra" : "attributes";
    }

    // Emit event with facet and value only
    eventBus.emit(EVENTS.FILTER_TOGGLED, {
      facet: actualFacetKey,
      value: sanitizedValue,
      checked: checked,
    });
  };

  // Check if a filter is selected
  const isFilterSelected = (
    facet: BasicFacet | AttributeFacet,
    value: BasicFacetValue
  ): boolean => {
    if (facet.type.toLowerCase() === "attribute") {
      return (
        selectedFilters["attributes"]?.has(
          sanitizeAttributeIndexValue(value.indexValue || "")
        ) ||
        selectedFilters["attributes_extra"]?.has(
          sanitizeAttributeIndexValue(value.indexValue || "")
        ) ||
        false
      );
    }
    const sanitizedValue = sanitizeFilterValue(value.filter);
    return selectedFilters[facet.name]?.has(sanitizedValue) || false;
  };

  const handleClick = () => {
    const mobileFilters =
      document.querySelector<HTMLElement>(".category-filters");
    const activeFilters =
      document.querySelector<HTMLElement>(".active-filters");
    const mobileFilterButton = document.querySelector<HTMLButtonElement>(
      ".mobile-filter__trigger"
    );
    if (!mobileFilterButton || !mobileFilters) {
      return;
    }
    if (mobileFilterButton.classList.contains("mobile-filter__trigger--open")) {
      mobileFilters.classList.add("filters-collapsed");
      activeFilters?.classList.add("filters-collapsed");
      mobileFilterButton.classList.remove("mobile-filter__trigger--open");
    } else {
      mobileFilters.classList.remove("filters-collapsed");
      activeFilters?.classList.remove("filters-collapsed");
      mobileFilterButton.classList.add("mobile-filter__trigger--open");
    }
  };

  useEffect(() => {
    const setupMobileFilters = () => {
      const mobileFilters =
        document.querySelector<HTMLElement>(".category-filters");
      const activeFilters =
        document.querySelector<HTMLElement>(".active-filters");
      if (window.matchMedia("(max-width: 1024px)").matches) {
        mobileFilters?.classList.add("filters-collapsed");
        activeFilters?.classList.add("filters-collapsed");
      }
      return () => {
        // No cleanup needed since we're just adding classes
        // The classes will be removed when component unmounts
      };
    };
    const interval = setInterval(() => {
      if (!window.matchMedia("(max-width: 1024px)").matches) {
        clearInterval(interval);
        return;
      }
      if (document.querySelector<HTMLElement>(".category-filters")) {
        setupMobileFilters();
        clearInterval(interval);
        return;
      }
    }, 400);

    const cleanup = setupMobileFilters();

    return () => {
      cleanup?.();
    };
  }, []);

  if (loading) {
    return null;
  }

  // If no facets are available
  if (!facets || Object.keys(facets).length === 0) {
    return null;
  }

  return (
    <div>
      {showMobileTrigger && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleClick();
          }}
          className="mobile-filter__trigger"
        >
          Filters
        </button>
      )}
      <div className="bg-white category-filters pt-4">
        {Object.entries(facets)
          .sort(([, a], [, b]) => (a.weight || 0) - (b.weight || 0))
          .map(([facetKey, facet]) => {
            if (
              facet.type.toLocaleLowerCase() === "attribute" ||
              facet.type.toLocaleLowerCase() === "shop" ||
              facet.values.length === 0
            ) {
              return null;
            }
            const facetName = facet.label || facet.name;
            const facetFilterName = facetKey;

            return (
              <div key={facetKey} className="mb-2 border-b pb-2">
                <span className="pb-2">
                  <b style={{ textTransform: "capitalize" }}>{facetName}</b>
                </span>
                <BasicFacetComponent
                  facet={facet as BasicFacet}
                  facetKey={facetFilterName}
                  selectedFilters={selectedFilters}
                  onFilterChange={handleFilterChange}
                  sanitizeFilterValue={sanitizeFilterValue}
                  isFilterSelected={isFilterSelected}
                />
              </div>
            );
          })}
        <div key="shopsfilter" className="mb-4 border-b py-2">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleFacet("shopsfilter");
            }}
            className="flex justify-between text-left w-full font-medium text-gray-800 mb-2"
          >
            <span>Raspoloživo po poslovnicama</span>
            <span className="text-gray-500">
              {expandedFacets["shopsfilter"] ? "−" : "+"}
            </span>
          </button>
          <ShopsFacetCard
            groupId={groupId}
            isExpanded={expandedFacets["shopsfilter"] || false}
            expandedFacets={expandedFacets}
          />
        </div>

        {Object.entries(facets)
          .sort(([, a], [, b]) => (a.weight || 0) - (b.weight || 0))
          .map(([facetKey, facet]) => {
            if (
              facet.type.toLowerCase() !== "attribute" ||
              facet.type.toLowerCase() !== "shop" ||
              !facet.isExtra ||
              facet.values.length === 0
            ) {
              return null;
            }
            const facetName = facet.label || facet.name;
            const facetFilterName = "attributes";

            return (
              <div key={facetKey} className="mb-4 border-b py-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleFacet(facetKey);
                  }}
                  className="flex justify-between items-top w-full font-medium text-gray-800 mb-2"
                >
                  <span>{facetName}</span>
                  <span className="text-gray-500">
                    {expandedFacets[facetKey] ? "−" : "+"}
                  </span>
                </button>
                <AttributeFacetComponent
                  facet={facet as AttributeFacet}
                  facetKey={facetFilterName}
                  isExpanded={expandedFacets[facetKey] === true}
                  selectedFilters={selectedFilters}
                  onFilterChange={handleFilterChange}
                  sanitizeFilterValue={sanitizeFilterValue}
                  isFilterSelected={isFilterSelected}
                />
              </div>
            );
          })}
        {Object.entries(facets)
          .sort(([, a], [, b]) => (a.weight || 0) - (b.weight || 0))
          .map(([facetKey, facet]) => {
            if (
              facet.type.toLocaleLowerCase() !== "attribute" ||
              facet.type.toLowerCase() === "shop" ||
              facet.values.length === 0 ||
              facet.isExtra
            ) {
              return null;
            }
            const facetName = facet.label || facet.name;
            const facetFilterName = "attributes";

            return (
              <div key={facetKey} className="mb-2 border-b py-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleFacet(facetKey);
                  }}
                  className="flex justify-between text-left items-top w-full font-medium text-gray-800 mb-1"
                >
                  <span>{facetName}</span>
                  <span className="text-gray-500">
                    {expandedFacets[facetKey] ? "−" : "+"}
                  </span>
                </button>
                <AttributeFacetComponent
                  facet={facet as AttributeFacet}
                  facetKey={facetFilterName}
                  isExpanded={expandedFacets[facetKey] === true}
                  selectedFilters={selectedFilters}
                  onFilterChange={handleFilterChange}
                  sanitizeFilterValue={sanitizeFilterValue}
                  isFilterSelected={isFilterSelected}
                />
              </div>
            );
          })}
      </div>
    </div>
  );
};

export const FacetCard = FacetCardNew;
